import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';

import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Rating,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  LinearProgress,
  Paper,
  Divider,
  IconButton,
  Badge,
  Skeleton,
  ListItemButton,
  useTheme,
  useMediaQuery,
  Fab,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  Button,
  Collapse,
  Alert
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Comment as CommentIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Sort as SortIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { useSpReviewLikeMutation } from '../../../redux/thunks/sp/spreviewlike';
import { useGetSpReviewQuery } from '../../../redux/thunks/sp/spreviewget';
import { useGetReviewsByBusinessIdQuery } from '../../../redux/thunks/unauth/getreviewsbybusinessid';

// 🆕 Mobile Filter & Stats Drawer Component
const MobileFilterStatsDrawer = ({ 
  isOpen, 
  onClose,
  searchQuery,
  setSearchQuery,
  selectedRating,
  setSelectedRating,
  selectedTags,
  setSelectedTags,
  sortBy,
  setSortBy,
  ratingDistribution,
  reviews,
  filteredAndSortedReviews
}) => {
  const [expandedSections, setExpandedSections] = useState({
    search: true,
    rating: false,
    tags: false,
    sort: false,
    stats: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={isOpen}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: { 
          height: '85vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          bgcolor: 'background.default'
        }
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: 40,
          height: 4,
          bgcolor: 'grey.300',
          borderRadius: 2,
          mx: 'auto',
          mt: 1,
          mb: 2
        }}
      />

      <Box sx={{ px: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Filters & Statistics
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflow: 'auto', height: 'calc(85vh - 80px)' }}>
          {/* Search Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('search')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Search Reviews</Typography>
              </Box>
              {expandedSections.search ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.search}>
              <CardContent sx={{ pt: 0 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search reviews..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </CardContent>
            </Collapse>
          </Card>

          {/* Rating Filter Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('rating')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Filter by Rating</Typography>
              </Box>
              {expandedSections.rating ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.rating}>
              <CardContent sx={{ pt: 0 }}>
                <List disablePadding>
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <ListItemButton
                      key={rating}
                      selected={selectedRating === rating}
                      onClick={() => setSelectedRating(rating === selectedRating ? 0 : rating)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <Rating value={rating} readOnly size="small" />
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              ({ratingDistribution[rating] || 0})
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </CardContent>
            </Collapse>
          </Card>

          {/* Experience Tags Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('tags')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Experience Tags</Typography>
              </Box>
              {expandedSections.tags ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.tags}>
              <CardContent sx={{ pt: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {EXPERIENCE_TAGS.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      clickable
                      color={selectedTags.includes(tag) ? "primary" : "default"}
                      onClick={() => {
                        setSelectedTags(prev =>
                          prev.includes(tag)
                            ? prev.filter(t => t !== tag)
                            : [...prev, tag]
                        );
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Collapse>
          </Card>

          {/* Sort Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('sort')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SortIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Sort Options</Typography>
              </Box>
              {expandedSections.sort ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.sort}>
              <CardContent sx={{ pt: 0 }}>
                <FormControl fullWidth>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    {SORT_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Collapse>
          </Card>

          {/* Statistics Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent 
              sx={{ 
                pb: 1, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onClick={() => toggleSection('stats')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AnalyticsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Review Statistics</Typography>
              </Box>
              {expandedSections.stats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </CardContent>
            
            <Collapse in={expandedSections.stats}>
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Rating Distribution
                </Typography>
                {[5, 4, 3, 2, 1].map((rating) => (
                  <Box key={rating} sx={{ mb: 1 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Rating value={rating} readOnly size="small" />
                      <Typography variant="body2">
                        {ratingDistribution[rating] || 0}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={((ratingDistribution[rating] || 0) / reviews.length) * 100}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                ))}

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                  Review Metrics
                </Typography>
                <List dense disablePadding>
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Average Rating"
                      secondary={
                        (reviews.reduce((acc, review) => acc + review.rating, 0) / 
                        reviews.length).toFixed(1)
                      }
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Total Reviews"
                      secondary={reviews.length}
                    />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemText
                      primary="Verified Reviews"
                      secondary={`${reviews.filter(r => r.verified).length} (${
                        Math.round((reviews.filter(r => r.verified).length / reviews.length) * 100)
                      }%)`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

// 🆕 Mobile Review Summary Component
const MobileReviewSummary = ({ reviews, filteredAndSortedReviews, ratingDistribution }) => {
  const averageRating = reviews.length > 0 
    ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
            Customer Reviews
          </Typography>
          <Typography variant="body2" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
            {filteredAndSortedReviews.length} of {reviews.length} reviews
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rating 
              value={parseFloat(averageRating)} 
              readOnly 
              size="small" 
              sx={{ color: 'primary.contrastText' }}
            />
            <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
              {averageRating}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.8 }}>
            Average rating
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// 🆕 Mobile Header Component
const MobileReviewHeader = ({ onFilterClick, sortBy, setSortBy, filteredLength }) => {
  return (
    <Paper sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="h6">
        Reviews ({filteredLength})
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <IconButton 
          color="primary" 
          onClick={onFilterClick}
          sx={{ 
            border: '1px solid',
            borderColor: 'primary.main'
          }}
        >
          <FilterIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

// Skeleton Components (unchanged)
const LeftColumnSkeleton = () => (
  <Grid item xs={12} md={3}>
    <Paper sx={{ p: 2, mb: 2 }}>
      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={56} sx={{ mb: 3 }} />
      
      <Skeleton variant="text" width="40%" height={24} sx={{ mb: 1 }} />
      {[1, 2, 3, 4, 5].map((item) => (
        <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Skeleton variant="rectangular" width={120} height={24} />
          <Skeleton variant="text" width={30} height={24} sx={{ ml: 1 }} />
        </Box>
      ))}
      
      <Skeleton variant="text" width="40%" height={24} sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {[1, 2, 3, 4].map((item) => (
          <Skeleton key={item} variant="rounded" width={80} height={32} />
        ))}
      </Box>
    </Paper>

    <Paper sx={{ p: 2, mb: 2 }}>
      <Skeleton variant="text" width="80%" height={32} sx={{ mb: 2 }} />
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={60} />
        </Box>
      ))}
    </Paper>
  </Grid>
);

const MiddleColumnSkeleton = () => (
  <Grid item xs={12} md={6}>
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rectangular" width={150} height={40} />
      </Box>
    </Paper>

    {[1, 2, 3, 4].map((item) => (
      <Paper key={item} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={100} height={20} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Skeleton variant="rectangular" width={120} height={24} />
          </Box>
        </Box>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="80%" height={20} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Skeleton variant="text" width={60} height={20} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="circular" width={40} height={40} />
        </Box>
      </Paper>
    ))}

    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
      <Skeleton variant="rectangular" width={300} height={36} />
    </Box>
  </Grid>
);

const RightColumnSkeleton = () => (
  <Grid item xs={12} md={3}>
    <Paper sx={{ p: 2, mb: 2 }}>
      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
      
      {[1, 2, 3, 4, 5].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Skeleton variant="rectangular" width={100} height={24} />
            <Skeleton variant="text" width={30} height={24} />
          </Box>
          <Skeleton variant="rectangular" width="100%" height={4} />
        </Box>
      ))}

      <Skeleton variant="text" width="60%" height={32} sx={{ mt: 3, mb: 2 }} />
      {[1, 2, 3].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Skeleton variant="text" width="100%" height={24} />
          <Skeleton variant="text" width="60%" height={20} />
        </Box>
      ))}
    </Paper>
  </Grid>
);

// Review Interaction Section (unchanged)
const ReviewInteractionSection = ({ review }) => {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const token = useSelector((state) => state.auth?.token || null);
  const reviewLiked = useSelector((state) => state.auth?.user?.reviewsLiked || [], shallowEqual);
  const reviewDisliked = useSelector((state) => state.auth?.user?.reviewsDisliked || [], shallowEqual);

  const comments = review.comments || [];

  const [thumbsUpPressed, setThumbsUpPressed] = useState(reviewLiked.includes(review._id));
  const [thumbsDownPressed, setThumbsDownPressed] = useState(reviewDisliked.includes(review._id));
  const [thisToken, setThisToken] = useState(null);

  const [likeReview, { isLoading: likeDislikeReviewLoading }] = useSpReviewLikeMutation();

  useEffect(() => {
    if (authStatus === 'authenticated') {
      setThisToken(token);
    }
  }, [authStatus, token, reviewLiked]);

  const handleCommentOnReview = () => {
    navigate(`/review/comments/${review._id}`, { state: review });
  };

  const handleLikeAction = async (actionType) => {
    if(!thisToken) {
      navigate('/auth/signin', { state: 'tbd' });
    }
    const payload = {
      auth: {
        token: token,
        authType: 'token'
      },
      reviewId: review._id,
      action: actionType
    };

    if (actionType === 'like') {
      setThumbsUpPressed(true);
      setThumbsDownPressed(false);
    } else if (actionType === 'dislike') {
      setThumbsDownPressed(true);
      setThumbsUpPressed(false);
    } else if (actionType === 'unlike') {
      setThumbsUpPressed(false);
    } else if (actionType === 'undislike') {
      setThumbsDownPressed(false);
    }

    await likeReview({ payload });
  };

  const handleThumbsUpPress = () => {
    if(!thisToken) {
      navigate('/auth/signin', { state: 'tbd' });
    }
    if (thumbsUpPressed) {
      handleLikeAction('unlike');
    } else {
      handleLikeAction('like');
      if (thumbsDownPressed) {
        handleLikeAction('undislike');
      }
    }
  };

  const handleThumbsDownPress = () => {
    if(!thisToken) {
      navigate('/auth/signin', { state: 'tbd' });
    }
    if (thumbsDownPressed) {
      handleLikeAction('undislike');
    } else {
      handleLikeAction('dislike');
      if (thumbsUpPressed) {
        handleLikeAction('unlike');
      }
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            color={thumbsUpPressed ? 'primary' : 'default'} 
            aria-label="Thumb Up" 
            onClick={handleThumbsUpPress}
            size="small"
          >
            <ThumbUpIcon />
          </IconButton>
          
          <IconButton 
            color={thumbsDownPressed ? 'error' : 'default'} 
            aria-label="Thumb Down" 
            onClick={handleThumbsDownPress}
            size="small"
          >
            <ThumbDownIcon />
          </IconButton>
          
          <IconButton
            onClick={handleCommentOnReview}
            size="small"
          >
            <Badge badgeContent={comments.length} color="error">
              <CommentIcon />
            </Badge>
          </IconButton>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
    </>
  );
};

// Constants (unchanged)
const EXPERIENCE_TAGS = [
  'Coffee',
  'Pastries',
  'Atmosphere',
  'Service',
  'Value',
  'Cleanliness'
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Most Recent' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'likes', label: 'Most Helpful' }
];

const ITEMS_PER_PAGE = 5;

export default function ReviewsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const token = useSelector((state) => state.auth?.token || null);
  const { businessId: urlBusinessId } = useParams();

  // 🆕 Mobile state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // State (unchanged)
  const [businessId, setBusinessId] = useState(urlBusinessId);  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRating, setSelectedRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [thisToken, setThisToken] = useState(null);

  const args = {
    authType: 'none',
    token: token,
    businessId: businessId
  }
  const { data: reviews = [], error: reviewError, isLoading: reviewLoading } = useGetReviewsByBusinessIdQuery(args);

  // All existing useEffect hooks (unchanged)
  useEffect(() => {
    if (reviews) {
      console.log('Loading reviews ', reviews);
    }
  }, [reviews]);

  useEffect(() => {
    setLoading(reviewLoading);
  }, [reviewLoading]);

  useEffect(() => {
    if (urlBusinessId !== businessId) {
      setBusinessId(urlBusinessId);
    }
  }, [urlBusinessId]);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      setThisToken(token);
    }
  }, [authStatus, token]);

  // All existing computed values (unchanged)
  const ratingDistribution = useMemo(() => {
    if (!reviews || reviews.length === 0) return {};
    return reviews.reduce((acc, review) => {
      acc[review.rating] = (acc[review.rating] || 0) + 1;
      return acc;
    }, {});
  }, [reviews]);

  const filteredAndSortedReviews = useMemo(() => {
    let result = [...reviews];

    if (searchQuery) {
      result = result.filter(review => 
        review.review.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.author.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedRating > 0) {
      result = result.filter(review => review.rating === selectedRating);
    }

    if (selectedTags.length > 0) {
      result = result.filter(review => 
        selectedTags.every(tag => review.tags.includes(tag))
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'likes':
          return b.likes - a.likes;
        case 'date':
        default:
          return b.date - a.date;
      }
    });

    return result;
  }, [reviews, searchQuery, selectedRating, selectedTags, sortBy]);

  const totalPages = Math.ceil(filteredAndSortedReviews.length / ITEMS_PER_PAGE);
  const paginatedReviews = filteredAndSortedReviews.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handlePageChange = (event, value) => {
    setLoading(true);
    setPage(value);
    setTimeout(() => setLoading(false), 500);
  };

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          minHeight: '100vh',
          pt: isMobile ? '48px' : 2,
          pb: isMobile ? 0 : 2
        }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            <LeftColumnSkeleton />
            <MiddleColumnSkeleton />
            <RightColumnSkeleton />
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        minHeight: '100vh',
        pt: isMobile ? '48px' : 0,
        pb: isMobile ? 0 : 2
      }}
    >
      {isMobile ? (
        // 🆕 Mobile Layout
        <>
          {/* Mobile Review Summary */}
          <Box sx={{ p: 1 }}>
            <MobileReviewSummary 
              reviews={reviews}
              filteredAndSortedReviews={filteredAndSortedReviews}
              ratingDistribution={ratingDistribution}
            />

            {/* Mobile Header with Sort & Filter */}
            <MobileReviewHeader 
              onFilterClick={() => setFilterDrawerOpen(true)}
              sortBy={sortBy}
              setSortBy={setSortBy}
              filteredLength={filteredAndSortedReviews.length}
            />

            {/* Reviews List */}
            <Box sx={{ mb: 1 }}>
              {paginatedReviews.length > 0 ? (
                paginatedReviews.map((review) => (
                  <Paper
                    key={review.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, border: 'none', borderRadius: 0 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {review.userDisplayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.created).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Rating
                          value={Math.round(review.rating * 10) / 10}
                          precision={0.1}
                          readOnly
                          size="small"
                        />
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          {Math.round(review.rating * 10) / 10}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.4 }}>
                      {review.review}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {review.likes.length} Likes
                      </Typography>
                      {review.verified && (
                        <Chip
                          label="Verified"
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    <ReviewInteractionSection review={review} />
                  </Paper>
                ))
              ) : (
                <Alert severity="info" sx={{ m: 2 }}>
                  No reviews found matching your filters.
                </Alert>
              )}
            </Box>

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Box>

          {/* Mobile Filter Drawer */}
          <MobileFilterStatsDrawer
            isOpen={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            sortBy={sortBy}
            setSortBy={setSortBy}
            ratingDistribution={ratingDistribution}
            reviews={reviews}
            filteredAndSortedReviews={filteredAndSortedReviews}
          />
        </>
      ) : (
        // 🆕 Desktop Layout - Original 3 Columns (unchanged)
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            {/* Left Column - Filters */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Filter Reviews
                  </Typography>

                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search reviews..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ mb: 3 }}
                  />

                  <Typography variant="subtitle2" gutterBottom>
                    Filter by Rating
                  </Typography>
                  <List>
                    {[5, 4, 3, 2, 1].map((rating) => (
                      <ListItemButton
                        key={rating}
                        selected={selectedRating === rating}
                        onClick={() => setSelectedRating(rating === selectedRating ? 0 : rating)}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center">
                              <Rating value={rating} readOnly size="small" />
                              <Typography variant="body2" sx={{ ml: 1 }}>
                                ({ratingDistribution[rating] || 0})
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Experience Tags
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {EXPERIENCE_TAGS.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        clickable
                        color={selectedTags.includes(tag) ? "primary" : "default"}
                        onClick={() => {
                          setSelectedTags(prev =>
                            prev.includes(tag)
                              ? prev.filter(t => t !== tag)
                              : [...prev, tag]
                          );
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Middle Column - Reviews */}
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                      Customer Reviews ({filteredAndSortedReviews.length})
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        {SORT_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>

              {paginatedReviews.map((review) => (
                <Paper
                  key={review.id}
                  variant="outlined"
                  sx={{ p: 2, mb: 2, border: 'none' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1">{review.userDisplayName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(review.created).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Rating
                        value={Math.round(review.rating * 10) / 10}
                        precision={0.1}
                        readOnly
                        size="small"
                      />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {Math.round(review.rating * 10) / 10}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {review.review}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {review.likes.length} Likes
                    </Typography>
                    {review.verified && (
                      <Chip
                        label="Verified"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <ReviewInteractionSection review={review} />
                </Paper>
              ))}

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Grid>

            {/* Right Column - Statistics */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Review Statistics
                  </Typography>

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Rating Distribution
                  </Typography>
                  {[5, 4, 3, 2, 1].map((rating) => (
                    <Box key={rating} sx={{ mb: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Rating value={rating} readOnly size="small" />
                        <Typography variant="body2">
                          {ratingDistribution[rating] || 0}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={((ratingDistribution[rating] || 0) / reviews.length) * 100}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  ))}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                    Review Metrics
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Average Rating"
                        secondary={
                          (reviews.reduce((acc, review) => acc + review.rating, 0) / 
                          reviews.length).toFixed(1)
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Reviews"
                        secondary={reviews.length}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Verified Reviews"
                        secondary={`${reviews.filter(r => r.verified).length} (${
                          Math.round((reviews.filter(r => r.verified).length / reviews.length) * 100)
                        }%)`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      )}
    </Box>
  );
}